import React, { useCallback, useState } from 'react'
import {
  Autocomplete,
  AutocompleteProps,
  AutocompleteRenderInputParams,
  FilterOptionsState,
} from '@mui/material'
import { createFilterOptions } from '@mui/material/Autocomplete'
import * as R from 'ramda'

import PuiTextField from './PuiTextField'

// Add "xxx" option created dynamically
const getOptionLabel = (option: string | AutocompleteIdObject): string => {
  if (typeof option === 'string') {
    return option // Handle string inputs
  }
  return option.inputValue || option.name || ''
}

export type AutocompleteIdObject = {
  id?: string
  inputValue?: string
  name?: string
}

export interface CreatableAutocompleteProps
  extends Omit<
  AutocompleteProps<string | AutocompleteIdObject, true, any, true>,
  'renderInput'
  > {
  readonly className?: string
  readonly createLabel?: string
  readonly label?: React.ReactNode
  readonly maxSuggestions?: number
  readonly minSearchLength?: number
  readonly onValueChange: (value: (string | AutocompleteIdObject)[]) => void
  readonly renderInput?: (
    params: AutocompleteRenderInputParams,
  ) => React.ReactNode
  readonly value?: (string | AutocompleteIdObject)[]
}

const CreatableAutocomplete = ({
  value: valueProp,
  onValueChange = R.F,
  disabled = false,
  className,
  label = '',
  createLabel = '',
  maxSuggestions = Infinity,
  minSearchLength = 0,
  renderInput,
  ...rest
}: CreatableAutocompleteProps) => {
  const value = valueProp || []

  const [searchValue, setSearchValue] = useState('')

  const getRenderOption = useCallback(
    (
      props: React.HTMLAttributes<HTMLLIElement>,
      option: string | AutocompleteIdObject,
    ) => (
      <li
        key={typeof option === 'string' ? option : option.id || option.name}
        {...props}
      >
        {typeof option === 'string'
          ? option
          : option.inputValue
            ? `${option.inputValue} ${createLabel}`
            : option.name}
      </li>
    ),
    [createLabel],
  )

  const filterOptions = useCallback(
    (
      options: (string | AutocompleteIdObject)[],
      params: FilterOptionsState<string | AutocompleteIdObject>,
    ) => {
      const { inputValue } = params

      const inputLength = inputValue?.length || 0
      if (inputLength < minSearchLength) {
        return []
      }

      const filterOptionsWithConfig = createFilterOptions<
      string | AutocompleteIdObject
      >({
        limit: maxSuggestions,
      })

      const optionsByParams = filterOptionsWithConfig(options, params)

      // Suggest the creation of a new value
      const isExistingInOptions = options.some(
        option => typeof option !== 'string' && inputValue === option.name,
      )
      const isExistingInSelectedOptions = value.some(
        item => typeof item !== 'string' && inputValue === item.name,
      )

      return inputValue !== '' &&
        !isExistingInOptions &&
        !isExistingInSelectedOptions
        ? [
          ...optionsByParams,
          {
            inputValue,
            name: inputValue,
          } as AutocompleteIdObject,
        ]
        : optionsByParams
    },
    [value, maxSuggestions, minSearchLength],
  )

  return (
    <Autocomplete
      filterSelectedOptions
      freeSolo
      multiple
      disabled={disabled}
      filterOptions={filterOptions}
      getOptionLabel={getOptionLabel}
      inputValue={searchValue}
      renderInput={
        renderInput ||
        (params => (
          <PuiTextField
            {...params}
            fullWidth
            className={className}
            label={label}
            variant="standard"
          />
        ))
      }
      renderOption={getRenderOption}
      value={value}
      onChange={(event, newValue) => onValueChange(newValue)}
      onInputChange={(event, newValue) => setSearchValue(newValue)}
      {...rest}
    />
  )
}

export default CreatableAutocomplete
